import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { PageBar, Content } from '../components/Layout'
import { Badge, Empty, ErrorBox, Loading, Panel, Stat, Stats } from '../components/ui'
import { api } from '../lib/api'
import { gbp, shortDate } from '../lib/format'
import type { AppSettings, Client, Invoice } from '../types'

export function Invoices() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['invoices'], queryFn: () => api.get<Invoice[]>('/invoices'),
  })
  const clients = useQuery({ queryKey: ['clients'], queryFn: () => api.get<Client[]>('/clients') })
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => api.get<AppSettings>('/settings') })

  const outstanding = (data ?? []).filter((i) => i.status !== 'paid')
    .reduce((s, i) => s + i.gross_pence, 0)
  const paid = (data ?? []).filter((i) => i.status === 'paid')
    .reduce((s, i) => s + i.gross_pence, 0)

  return (
    <>
      <PageBar title="Invoices" />
      <Content>
        <Stats>
          <Stat label="Outstanding" value={gbp(outstanding)} tone={outstanding ? 'alert' : undefined} note="Unpaid" />
          <Stat label="Paid" value={gbp(paid)} tone="ok" note="Settled to date" />
          <Stat
            label="VAT"
            value={<span style={{ fontSize: 26 }}>
              {settings.data?.vat_registered ? `${settings.data.vat_rate_pct}%` : 'OFF'}
            </span>}
            note={settings.data?.vat_registered ? 'Applied to all invoices' : 'Not registered — confirm with Jerry'}
          />
        </Stats>

        <Panel>
          {isLoading && <Loading />}
          {error && <ErrorBox error={error} />}
          {data && !data.length && <Empty>No invoices yet. Create one from a booking.</Empty>}
          {data && data.length > 0 && (
            <table className="cards">
              <thead>
                <tr>
                  <th style={{ width: 130 }}>Number</th><th>Description</th>
                  <th style={{ width: 170 }}>Client</th>
                  <th style={{ width: 100 }}>Issued</th>
                  <th style={{ width: 100 }}>Total</th>
                  <th style={{ width: 100 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.map((i) => (
                  <tr key={i.id}>
                    <td><Link to={`/invoices/${i.id}`}><b>{i.number}</b></Link></td>
                    <td className="snippet">{i.description}</td>
                    <td data-label="Client">{clients.data?.find((c) => c.id === i.client_id)?.name ?? <span className="muted">—</span>}</td>
                    <td className="when" data-label="Issued">{shortDate(i.issued_on)}</td>
                    <td data-label="Total"><b>{gbp(i.gross_pence)}</b></td>
                    <td><Badge kind="inv" value={i.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      </Content>
    </>
  )
}
